import React, { useEffect, useMemo, useState } from "react";
import { Button, FormGroup, Label, Spinner } from "reactstrap";
import Select from "react-select";
import Creatable from "react-select/creatable";

import { Aviso, ModalHub, fmtNumber } from "../_documentosFrete/comum";
import { makeReactSelectStyles, reactSelectPortalProps } from "../../../utils/reactSelectStyles";
import { aplicarMarcadores, listarMarcadores, mensagemErroPedido } from "../../../services/pedidosApi";

// Marcadores do pedido (H2, PO1-2376), para 1 ou N pedidos: `adicionar` é um
// Creatable (marcador novo ou existente do embarcador), `remover` lista os
// marcadores já usados (nos pedidos selecionados, quando a tela os conhece; senão
// os do embarcador). POST /pedidos/marcadores/lote.

const selectStyles = makeReactSelectStyles({ portalZIndex: 1070 });
export const MAX_MARCADOR = 40;
export const ERRO_VIRGULA = "Marcador não pode ter vírgula (,): ela separa um marcador do outro na listagem.";

export const opcao = (m) => ({ value: m, label: m });

// Normaliza o texto digitado como o backend (trim, espaços simples, até 40).
export function normalizarMarcador(texto) {
  return String(texto || "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, MAX_MARCADOR);
}

// Mensagem de erro de um marcador digitado; "" quando ele vale. A vírgula é o
// separador da coluna `marcadores` da listagem, então o backend devolve 400.
export function erroDoMarcador(texto) {
  if (String(texto || "").indexOf(",") !== -1) return ERRO_VIRGULA;
  return "";
}

export default function MarcadoresModal({ pedidos, marcadoresAtuais = [], onClose }) {
  const lista = Array.isArray(pedidos) ? pedidos : [];
  const [existentes, setExistentes] = useState([]);
  const [adicionar, setAdicionar] = useState([]);
  const [remover, setRemover] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [alterou, setAlterou] = useState(false);
  const [erroDigitado, setErroDigitado] = useState("");

  useEffect(() => {
    let ativo = true;
    listarMarcadores()
      .then((r) => ativo && setExistentes(r.map((m) => m.marcador).filter(Boolean)))
      .catch(() => ativo && setExistentes([]));
    return () => {
      ativo = false;
    };
  }, []);

  const opcoesExistentes = useMemo(() => existentes.map(opcao), [existentes]);
  // Para remover: os marcadores já presentes nos pedidos selecionados; sem essa
  // informação (seleção em massa sem chips), qualquer marcador do embarcador.
  const opcoesRemover = useMemo(() => {
    const base = marcadoresAtuais && marcadoresAtuais.length ? marcadoresAtuais : existentes;
    return base.map(opcao);
  }, [marcadoresAtuais, existentes]);

  const aplicar = async () => {
    if (salvando) return;
    const add = adicionar.map((o) => normalizarMarcador(o.value)).filter(Boolean);
    const rem = remover.map((o) => normalizarMarcador(o.value)).filter(Boolean);
    if (!add.length && !rem.length) {
      setAviso({ tom: "erro", texto: "Informe marcadores para adicionar ou remover." });
      return;
    }
    const invalido = add.concat(rem).map(erroDoMarcador).filter(Boolean)[0];
    if (invalido) {
      setAviso({ tom: "erro", texto: invalido });
      return;
    }
    setSalvando(true);
    setAviso(null);
    try {
      const r = await aplicarMarcadores({ pedidos: lista, adicionar: add, remover: rem });
      setAlterou(true);
      const naoEncontrados = (r && r.naoEncontrados) || [];
      setAviso({
        tom: naoEncontrados.length ? "aviso" : "ok",
        texto:
          `${fmtNumber(r.atualizados || 0)} pedido(s) atualizado(s)` +
          (add.length ? ` · ${fmtNumber(r.adicionados || 0)} marcador(es) adicionado(s)` : "") +
          (rem.length ? ` · ${fmtNumber(r.removidos || 0)} removido(s)` : "") +
          (naoEncontrados.length ? ` · não encontrado(s): ${naoEncontrados.join(", ")}` : "") +
          ".",
      });
      setAdicionar([]);
      setRemover([]);
      const novos = add.filter((m) => existentes.indexOf(m) === -1);
      if (novos.length) setExistentes(existentes.concat(novos));
    } catch (e) {
      setAviso({ tom: "erro", texto: mensagemErroPedido(e, "Não foi possível aplicar os marcadores.") });
    } finally {
      setSalvando(false);
    }
  };

  const fechar = () => {
    if (salvando) return;
    onClose(alterou);
  };

  const titulo = lista.length === 1 ? `Marcadores · pedido ${lista[0]}` : `Marcadores · ${fmtNumber(lista.length)} pedido(s)`;

  return (
    <ModalHub titulo={titulo} icone="tim-icons icon-tag" onClose={fechar} maxWidth={640}>
      <div className="docs-frete__form">
        {lista.length === 1 && marcadoresAtuais.length ? (
          <div className="pedidos__chips" aria-label="Marcadores atuais">
            {marcadoresAtuais.map((m) => (
              <span key={m} className="pedidos__chip">
                {m}
              </span>
            ))}
          </div>
        ) : null}
        <FormGroup>
          <Label htmlFor="pedido-marcadores-adicionar">Adicionar</Label>
          <Creatable
            inputId="pedido-marcadores-adicionar"
            isMulti
            classNamePrefix="react-select"
            styles={selectStyles}
            options={opcoesExistentes}
            value={adicionar}
            onChange={(v) => setAdicionar(v || [])}
            placeholder="Digite para criar ou escolha um marcador..."
            onInputChange={(texto, meta) => {
              // Só o que o usuário digita; limpar/fechar não é erro.
              if (meta && meta.action === "input-change") setErroDigitado(erroDoMarcador(texto));
              else setErroDigitado("");
            }}
            noOptionsMessage={({ inputValue }) => erroDoMarcador(inputValue) || "Digite para criar um marcador"}
            formatCreateLabel={(texto) => `Criar "${normalizarMarcador(texto)}"`}
            getNewOptionData={(texto) => opcao(normalizarMarcador(texto))}
            isValidNewOption={(texto, _valor, opcoes) => {
              const n = normalizarMarcador(texto);
              if (!n || erroDoMarcador(n)) return false;
              return !opcoes.some((o) => String(o.value).toLowerCase() === n.toLowerCase());
            }}
            isDisabled={salvando}
            {...reactSelectPortalProps()}
          />
          {erroDigitado ? <span className="docs-frete__erro-campo">{erroDigitado}</span> : null}
          <span className="docs-frete__dica">Até {MAX_MARCADOR} caracteres, sem vírgula; maiúsculas e minúsculas não diferenciam.</span>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="pedido-marcadores-remover">Remover</Label>
          <Select
            inputId="pedido-marcadores-remover"
            isMulti
            classNamePrefix="react-select"
            styles={selectStyles}
            options={opcoesRemover}
            value={remover}
            onChange={(v) => setRemover(v || [])}
            placeholder="Marcadores a tirar dos pedidos..."
            noOptionsMessage={() => "Nenhum marcador para remover"}
            isDisabled={salvando}
            {...reactSelectPortalProps()}
          />
        </FormGroup>
      </div>

      {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={salvando}>
          Fechar
        </Button>
        <Button color="primary" size="sm" onClick={aplicar} disabled={salvando || !lista.length}>
          {salvando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
          {salvando ? "Aplicando..." : "Aplicar"}
        </Button>
      </div>
    </ModalHub>
  );
}
